// @system — role helpers shared by ProtectedRoute, navigation filtering and the
// Staff & roles page. Roles are DATA on the server (`roles` table); the client
// only needs to know the four system roles and their order. Permissions — not
// roles — decide what a user may do; the rank below exists so the legacy
// `requiredRole: 'admin'` gates keep working now that `owner` sits above
// `admin`.

/** Role given to every self-registered account. Never holds permissions. */
export const DEFAULT_ROLE = 'user'

/** Role that always holds every permission and whose matrix column is immutable. */
export const OWNER_ROLE = 'owner'

/** System roles ordered from least to most privileged. */
export const SYSTEM_ROLES = Object.freeze(['user', 'support', 'admin', 'owner'])

/** @type {Readonly<Record<string, number>>} */
const RANK = Object.freeze(Object.fromEntries(SYSTEM_ROLES.map((r, i) => [r, i])))

/**
 * True when `role` is at least as privileged as `required`. Unknown (DB-added)
 * roles only satisfy an exact match — they carry no implicit rank.
 * @param {string|null|undefined} role
 * @param {string|null|undefined} required
 * @returns {boolean}
 */
export function roleSatisfies(role, required) {
  if (!required) return true
  if (!role) return false
  if (role === required) return true
  const have = RANK[role]
  const need = RANK[required]
  if (have === undefined || need === undefined) return false
  return have >= need
}

/**
 * True when `role` may hold admin permissions (anything but the default member role).
 * @param {string|null|undefined} role
 * @returns {boolean}
 */
export function isStaffRole(role) {
  return !!role && role !== DEFAULT_ROLE
}
